/**
 * AdminUploadPage — upload design drafts for customer review.
 *
 * Two modes:
 *  A) Order-linked (recommended): search for a "Grafiker"-stage order,
 *     select it, then upload one image per item (all optional — skip items
 *     that share a graphic, e.g. 2 identical candles).
 *  B) Manual: no order selected — enter email/name/order# by hand and
 *     upload a single image (original behaviour, unchanged).
 *
 * Submitting creates a Firestore `drafts` doc which triggers
 * `sendCustomerEmail` automatically.
 */
import React, { useState, useEffect, useRef } from "react";
import { storage, db } from "../firebase";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import {
  doc, setDoc, updateDoc, serverTimestamp, collection,
  getDocs, query, where,
} from "firebase/firestore";

/**
 * Ein manueller Entwurf ueberholt einen wartenden Auto-Entwurf.
 *
 * Laedt die Grafikerin fuer eine Bestellung selbst etwas hoch, ist ein noch
 * ungeprüfter Kandidat gegenstandslos — er wuerde sonst in "Pruefung offen"
 * stehen bleiben und koennte versehentlich ein zweites Mal an denselben
 * Kunden gehen. Er wird deshalb auf "obsolete" gesetzt und landet im Archiv.
 *
 * Bewusst nur `pending_review`: `needs_manual` wandert ohnehin ins Archiv,
 * sobald die Bestellung den Grafiker-Schritt verlaesst, und `approved` ist
 * bereits beim Kunden.
 *
 * Fehlertolerant: der Upload darf daran nie scheitern.
 */
const obsoletePendingCandidates = async (orderNumber) => {
  if (!orderNumber) return 0;
  try {
    // Nur nach orderId filtern (Einzelfeld-Index) und den Status lokal
    // pruefen — das spart einen zusammengesetzten Index.
    const snap = await getDocs(query(
      collection(db, "draft_candidates"),
      where("orderId", "==", String(orderNumber)),
    ));
    const pending = snap.docs.filter(
      (d) => (d.data() || {}).status === "pending_review");
    await Promise.all(pending.map((d) => updateDoc(d.ref, {
      status:         "obsolete",
      // Schluessel, kein Anzeigetext: der Renderer validiert das Feld
      // streng gegen "manual_draft_exists" | "stage_advanced" | null.
      obsoleteReason: "manual_draft_exists",
    })));
    if (pending.length) {
      console.log(
        `${pending.length} Auto-Entwurf/Entwuerfe fuer #${orderNumber} ` +
        "als hinfaellig markiert.");
    }
    return pending.length;
  } catch (err) {
    console.warn("Auto-Entwuerfe konnten nicht als hinfaellig markiert " +
      "werden (unkritisch):", err);
    return 0;
  }
};

// ── Shared styles ─────────────────────────────────────────────────────────────

const inputStyle = {
  display: "block",
  width: "100%",
  padding: "0.5em 0.75em",
  marginTop: "0.35em",
  marginBottom: "1.25em",
  border: "1px solid #cbd5e0",
  borderRadius: "6px",
  fontSize: "0.95rem",
  boxSizing: "border-box",
};

const labelStyle = {
  display: "block",
  fontWeight: "600",
  fontSize: "0.875rem",
  color: "#2d3748",
};

// ── Helper: "Maria Mustermann" → "Mustermann, Maria" ─────────────────────────

const formatBuyerName = (buyerName) => {
  if (!buyerName) return "";
  const parts = buyerName.trim().split(/\s+/);
  if (parts.length === 1) return parts[0];
  const last  = parts[parts.length - 1];
  const first = parts.slice(0, -1).join(" ");
  return `${last}, ${first}`;
};

// ── Main component ────────────────────────────────────────────────────────────

const AdminUploadPage = () => {
  // Order picker state
  const [grafOrders, setGrafOrders]       = useState([]);
  const [searchTerm, setSearchTerm]       = useState("");
  const [showDropdown, setShowDropdown]   = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const searchRef = useRef(null);

  // Form fields
  const [email, setEmail]                         = useState("");
  const [customerFirstName, setCustomerFirstName] = useState("");
  const [orderNumber, setOrderNumber]             = useState("");

  // Multi-image (order-linked mode)
  const [itemFiles, setItemFiles]       = useState([]);   // parallel to selectedOrder.items
  const [itemPreviews, setItemPreviews] = useState([]);

  // Extra manually-added image slots
  const [extraSlots, setExtraSlots] = useState([]);
  // Each: { file: null, preview: null, description: "" }

  // Single-image (manual mode)
  const [file, setFile]           = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);

  // Submit state
  const [uploading, setUploading]       = useState(false);
  const [uploadResult, setUploadResult] = useState(null);
  const [autoMatched, setAutoMatched]   = useState(false); // true when order was guessed from filename

  // Load Grafiker orders (+ optional test orders) once on mount
  useEffect(() => {
    const showTest = localStorage.getItem("showTestOrdersInUpload") === "true";
    const queries = [
      getDocs(query(collection(db, "etsy_orders"), where("workflowStage", "==", "grafiker"))),
      showTest
        ? getDocs(query(collection(db, "etsy_orders"), where("workflowStage", "==", "test")))
        : Promise.resolve(null),
    ];
    Promise.all(queries).then(([grafSnap, testSnap]) => {
      const grafikerOrders = grafSnap.docs.map((d) => ({ id: d.id, ...d.data() }));
      const testOrders = testSnap
        ? testSnap.docs.map((d) => ({ id: d.id, ...d.data(), _isTest: true }))
        : [];
      const all = [...grafikerOrders, ...testOrders].sort((a, b) =>
        formatBuyerName(a.buyerName).localeCompare(formatBuyerName(b.buyerName), "de"));
      setGrafOrders(all);
    }).catch(() => {});
  }, []);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handler = (e) => {
      if (searchRef.current && !searchRef.current.contains(e.target)) {
        setShowDropdown(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  // Filtered results for dropdown
  const term = searchTerm.trim().toLowerCase();
  const filteredOrders = term
    ? grafOrders.filter((o) =>
        (o.buyerName || "").toLowerCase().includes(term) ||
        o.id.includes(term),
      )
    : grafOrders;

  // Try to match a filename like "RussnakMichele_FABeige.png" against the
  // Grafiker order pool. Filename format: LastnameFirstname[_...][.ext]
  const guessOrderFromFilename = (filename) => {
    const namePart = filename.split(".")[0].split("_")[0].toLowerCase();
    if (namePart.length < 4) return null;
    for (const order of grafOrders) {
      const parts = (order.buyerName || "").trim().split(/\s+/);
      if (parts.length < 2) continue;
      const last  = parts[parts.length - 1].toLowerCase();
      const first = parts.slice(0, -1).join("").toLowerCase();
      const key   = last + first;
      if (namePart === key || namePart.startsWith(key) || key.startsWith(namePart)) {
        return order;
      }
    }
    return null;
  };

  // existingFile / existingPreview: file already chosen before order was selected
  // (avoids React batching issue when called synchronously after setFile)
  const selectOrder = (order, existingFile = null, existingPreview = null) => {
    setSelectedOrder(order);
    setSearchTerm("");
    setShowDropdown(false);
    // Auto-fill form fields
    const parts = (order.buyerName || "").trim().split(/\s+/);
    setCustomerFirstName(parts[0] || "");
    setEmail(order.customerEmail || "");
    setOrderNumber(order.id);
    // Initialise item slots — carry over any pre-selected file into slot 0
    const count = (order.items || []).length;
    const newFiles    = Array(count).fill(null);
    const newPreviews = Array(count).fill(null);
    const carryFile    = existingFile    ?? file;
    const carryPreview = existingPreview ?? previewUrl;
    if (carryFile && count > 0) {
      newFiles[0]    = carryFile;
      newPreviews[0] = carryPreview;
      setFile(null);
      setPreviewUrl(null);
    }
    setItemFiles(newFiles);
    setItemPreviews(newPreviews);
  };

  const clearSelection = () => {
    setSelectedOrder(null);
    setEmail("");
    setCustomerFirstName("");
    setOrderNumber("");
    setItemFiles([]);
    setItemPreviews([]);
    setExtraSlots([]);
    setAutoMatched(false);
  };

  const addExtraSlot = () =>
    setExtraSlots((prev) => [
      ...prev, { file: null, preview: null, description: "" },
    ]);

  const removeExtraSlot = (i) => {
    setExtraSlots((prev) => {
      if (prev[i]?.preview) URL.revokeObjectURL(prev[i].preview);
      return prev.filter((_, idx) => idx !== i);
    });
  };

  const updateExtraSlotFile = (i, f) => {
    setExtraSlots((prev) => {
      const next = [...prev];
      if (next[i].preview) URL.revokeObjectURL(next[i].preview);
      next[i] = {
        ...next[i],
        file:    f || null,
        preview: f ? URL.createObjectURL(f) : null,
      };
      return next;
    });
  };

  const updateExtraSlotDesc = (i, val) => {
    setExtraSlots((prev) => {
      const next = [...prev];
      next[i] = { ...next[i], description: val };
      return next;
    });
  };

  const updateItemFile = (i, f) => {
    setItemFiles((prev) => {
      const next = [...prev];
      next[i] = f || null;
      return next;
    });
    setItemPreviews((prev) => {
      const next = [...prev];
      if (prev[i]) URL.revokeObjectURL(prev[i]);
      next[i] = f ? URL.createObjectURL(f) : null;
      return next;
    });
  };

  // ── Submit ──────────────────────────────────────────────────────────────────

  const handleSubmit = async (e) => {
    e.preventDefault();
    setUploadResult(null);

    const isOrderMode = !!selectedOrder;

    if (!customerFirstName.trim()) {
      setUploadResult({ error: "Bitte Vorname eingeben." });
      return;
    }
    if (!orderNumber.trim()) {
      setUploadResult({ error: "Bitte Bestellnummer eingeben." });
      return;
    }

    if (isOrderMode) {
      const hasFile = itemFiles.some(Boolean) ||
        extraSlots.some((s) => s.file);
      if (!hasFile) {
        setUploadResult({ error: "Bitte mindestens eine Grafik hochladen." });
        return;
      }
    } else {
      if (!file || !email) {
        setUploadResult({ error: "Bitte E-Mail und Grafik angeben." });
        return;
      }
    }

    setUploading(true);

    try {
      let uploaderIp = null;
      try {
        const r = await fetch("https://api.ipify.org?format=json");
        uploaderIp = (await r.json()).ip;
      } catch { /* non-critical */ }

      const reviewId = doc(collection(db, "drafts")).id;

      if (isOrderMode) {
        // Multi-image path
        const images = [];
        for (let i = 0; i < itemFiles.length; i++) {
          const f = itemFiles[i];
          if (!f) continue;
          const fileRef = ref(storage, `drafts/${Date.now()}_${i}_${f.name}`);
          await uploadBytes(fileRef, f);
          const url = await getDownloadURL(fileRef);
          const item = (selectedOrder.items || [])[i] || {};
          images.push({
            imageUrl:        url,
            storagePath:     fileRef.fullPath,
            itemTitle:       item.title || null,
            personalization: item.personalization || null,
            // 2026-08-28: mit-speichern des strukturierten Multi-Field-
            // Personalization-Arrays damit DraftReviewForm die Labels
            // korrekt anzeigen kann (nicht nur den String).
            personalizations: Array.isArray(item.personalizations)
              ? item.personalizations : null,
          });
        }

        // Extra manually-added slots
        for (const slot of extraSlots) {
          if (!slot.file) continue;
          const xRef = ref(storage,
            `drafts/${Date.now()}_extra_${slot.file.name}`);
          await uploadBytes(xRef, slot.file);
          const xUrl = await getDownloadURL(xRef);
          images.push({
            imageUrl:        xUrl,
            storagePath:     xRef.fullPath,
            itemTitle:       slot.description.trim() || "Zusätzliche Grafik",
            personalization: null,
          });
        }

        await setDoc(doc(db, "drafts", reviewId), {
          customerEmail:     email,
          customerFirstName: customerFirstName.trim() || null,
          orderNumber:       orderNumber.trim() || null,
          imageUrl:          null,
          storagePath:       null,
          images,
          uploaderIp,
          createdAt:         serverTimestamp(),
        });
      } else {
        // Single-image path (manual mode)
        const fileRef = ref(storage, `drafts/${Date.now()}_${file.name}`);
        await uploadBytes(fileRef, file);
        const downloadURL = await getDownloadURL(fileRef);

        await setDoc(doc(db, "drafts", reviewId), {
          customerEmail:     email,
          customerFirstName: customerFirstName.trim() || null,
          orderNumber:       orderNumber.trim() || null,
          imageUrl:          downloadURL,
          storagePath:       fileRef.fullPath,
          uploaderIp,
          createdAt:         serverTimestamp(),
        });
      }

      // Wartende Auto-Entwuerfe derselben Bestellung sind jetzt hinfaellig.
      await obsoletePendingCandidates(orderNumber.trim() || null);

      setUploadResult({
        reviewId,
        orderNumber: orderNumber.trim() || null,
        error: null,
      });

      // Reset form
      clearSelection();
      setFile(null);
      if (previewUrl) { URL.revokeObjectURL(previewUrl); setPreviewUrl(null); }

    } catch (err) {
      console.error("Upload error:", err);
      setUploadResult({ error: "Upload fehlgeschlagen. Bitte erneut versuchen." });
    } finally {
      setUploading(false);
    }
  };

  // ── Render ──────────────────────────────────────────────────────────────────

  return (
    <div style={{ maxWidth: 580, margin: "0 auto" }}>
      <div style={{
        background: "#ffffff", borderRadius: "10px",
        boxShadow: "0 1px 4px rgba(0,0,0,0.08)", padding: "2em",
      }}>
        <h2 style={{ margin: "0 0 1.5em", fontSize: "1.25rem", color: "#1a202c" }}>
          Entwurf hochladen
        </h2>

        {/* ── Success banner ── */}
        {uploadResult?.reviewId && (
          <div style={{
            background: "#f0fff4", border: "1px solid #68d391",
            borderRadius: "6px", padding: "1em", marginBottom: "1.25em",
          }}>
            <p style={{ margin: "0 0 0.4em", fontWeight: "700", color: "#276749" }}>
              {uploadResult.orderNumber
                ? `Entwurf für Bestellung #${uploadResult.orderNumber} hochgeladen!`
                : "Entwurf erfolgreich hochgeladen!"}
            </p>
            <p style={{ margin: "0 0 0.75em", wordBreak: "break-all",
              fontSize: "0.875rem", color: "#2d6a4f" }}>
              Review-Link:{" "}
              <a href={`/review/${uploadResult.reviewId}`}
                target="_blank" rel="noreferrer">
                /review/{uploadResult.reviewId}
              </a>
            </p>
            <button
              type="button"
              style={{
                padding: "0.35em 0.9em", borderRadius: "5px",
                border: "1px solid #38a169", background: "#fff",
                color: "#276749", fontSize: "0.85rem", cursor: "pointer",
              }}
              onClick={() =>
                navigator.clipboard.writeText(
                  `${window.location.origin}/review/${uploadResult.reviewId}`)
              }
            >
              Link kopieren
            </button>
          </div>
        )}

        {/* ── Error banner ── */}
        {uploadResult?.error && (
          <div style={{
            background: "#fff5f5", border: "1px solid #fc8181",
            borderRadius: "6px", padding: "0.75em 1em", marginBottom: "1.25em",
            color: "#c53030", fontSize: "0.9rem",
          }}>
            {uploadResult.error}
          </div>
        )}

        <form onSubmit={handleSubmit}>

          {/* ── Order picker ── */}
          <div style={{ marginBottom: "1.5em" }}>
            <div style={labelStyle}>Bestellung (Grafiker-Stage)</div>

            {selectedOrder ? (
              <div style={{
                marginTop: "0.35em",
                padding: "0.6em 0.9em",
                border: "1px solid #B8A09A",
                borderRadius: "6px",
                background: "#fdf7f4",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                fontSize: "0.9rem",
              }}>
                <span style={{ color: "#2d3748", fontWeight: "600" }}>
                  {formatBuyerName(selectedOrder.buyerName)}
                  <span style={{ fontWeight: 400, color: "#718096",
                    marginLeft: "0.5em" }}>
                    #{selectedOrder.id}
                  </span>
                  {autoMatched && (
                    <span style={{
                      marginLeft: "0.6em", fontSize: "0.72rem",
                      fontWeight: "600", color: "#2f855a",
                      background: "#c6f6d5", borderRadius: "4px",
                      padding: "0.1em 0.4em",
                    }}>
                      ✓ aus Dateiname
                    </span>
                  )}
                </span>
                <button
                  type="button"
                  onClick={clearSelection}
                  style={{
                    background: "none", border: "none", color: "#a0aec0",
                    cursor: "pointer", fontSize: "0.82rem", padding: "0 0.2em",
                  }}
                >
                  × andere Bestellung
                </button>
              </div>
            ) : (
              <div ref={searchRef} style={{ position: "relative", marginTop: "0.35em" }}>
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(e) => {
                    setSearchTerm(e.target.value);
                    setShowDropdown(true);
                  }}
                  onFocus={() => setShowDropdown(true)}
                  placeholder="Name oder Bestellnummer suchen…"
                  style={{ ...inputStyle, marginBottom: 0 }}
                />
                {showDropdown && filteredOrders.length > 0 && (
                  <div style={{
                    position: "absolute", top: "100%", left: 0, right: 0,
                    background: "#fff", border: "1px solid #e2e8f0",
                    borderRadius: "6px", boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
                    zIndex: 100, maxHeight: "260px", overflowY: "auto",
                  }}>
                    {filteredOrders.map((o) => (
                      <div
                        key={o.id}
                        onMouseDown={() => { setAutoMatched(false); selectOrder(o); }}
                        style={{
                          padding: "0.55em 0.9em",
                          cursor: "pointer",
                          fontSize: "0.875rem",
                          borderBottom: "1px solid #f0f4f8",
                          color: "#2d3748",
                        }}
                        onMouseEnter={(e) =>
                          e.currentTarget.style.background = "#fdf7f4"}
                        onMouseLeave={(e) =>
                          e.currentTarget.style.background = "transparent"}
                      >
                        <span style={{ fontWeight: "600" }}>
                          {formatBuyerName(o.buyerName)}
                        </span>
                        <span style={{ color: "#a0aec0", marginLeft: "0.5em",
                          fontSize: "0.82rem" }}>
                          #{o.id}
                        </span>
                        {o._isTest && (
                          <span style={{ color: "#553c9a", fontSize: "0.72rem",
                            fontWeight: "700", marginLeft: "0.4em" }}>
                            [TEST]
                          </span>
                        )}
                        {(o.items || []).length > 0 && (
                          <span style={{ color: "#b0c4d8", marginLeft: "0.4em",
                            fontSize: "0.78rem" }}>
                            · {o.items.length} Artikel
                          </span>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* ── Auto-filled fields (read-only when order selected) ── */}
          <label style={labelStyle}>
            Kunden-E-Mail *
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              placeholder="kunde@example.com"
              style={inputStyle}
            />
          </label>

          <label style={labelStyle}>
            Vorname *
            <input
              type="text"
              value={customerFirstName}
              onChange={(e) => setCustomerFirstName(e.target.value)}
              placeholder="z. B. Maria"
              style={inputStyle}
            />
          </label>

          <label style={labelStyle}>
            Bestellnummer *
            <input
              type="text"
              value={orderNumber}
              onChange={(e) => setOrderNumber(e.target.value)}
              placeholder="z. B. 4021179364"
              style={inputStyle}
            />
          </label>

          {/* ── Per-item upload slots (order mode) ── */}
          {selectedOrder && (selectedOrder.items || []).length > 0 ? (
            <div style={{ marginBottom: "1.25em" }}>
              <div style={labelStyle}>
                Grafiken (PNG oder JPG)
                <span style={{ fontWeight: 400, color: "#718096",
                  marginLeft: "0.4em" }}>
                  — mind. 1 erforderlich
                </span>
              </div>
              {(selectedOrder.items || []).map((item, i) => (
                <div key={i} style={{
                  border: "1px solid #e2e8f0", borderRadius: "8px",
                  padding: "0.9em 1em", marginTop: "0.75em",
                  background: itemFiles[i] ? "#f0fff4" : "#fafafa",
                }}>
                  <div style={{
                    fontWeight: "600", fontSize: "0.875rem",
                    color: "#2d3748", marginBottom: "0.25em",
                  }}>
                    Artikel {i + 1}: {item.title}
                  </div>
                  {(() => {
                    const personFields =
                      Array.isArray(item.personalizations) &&
                      item.personalizations.length > 0
                        ? item.personalizations
                        : (item.personalization
                            ? [{ label: "Personalisierung",
                                 value: item.personalization, type: "text" }]
                            : []);
                    if (personFields.length === 0) return null;
                    return (
                      <div style={{
                        fontSize: "0.78rem", color: "#4a5568",
                        marginBottom: "0.5em",
                        background: "#fdf7f4",
                        borderLeft: "3px solid #B8A09A",
                        padding: "6px 10px",
                      }}>
                        {personFields.map((p, pi) => (
                          <div key={pi} style={{
                            marginBottom: pi < personFields.length - 1
                              ? "4px" : 0,
                          }}>
                            <strong style={{ color: "#5a4a44" }}>
                              {p.label || "Personalisierung"}:
                            </strong>{" "}
                            {p.type === "file"
                              ? <a href={p.value} target="_blank"
                                   rel="noopener noreferrer"
                                   style={{ color: "#B8A09A",
                                            textDecoration: "underline" }}>
                                  📎 Datei ansehen
                                </a>
                              : <span style={{ whiteSpace: "pre-wrap" }}>
                                  {p.value}
                                </span>}
                          </div>
                        ))}
                      </div>
                    );
                  })()}
                  <input
                    type="file"
                    accept="image/png, image/jpeg"
                    onChange={(e) => updateItemFile(i, e.target.files[0])}
                    style={{ fontSize: "0.875rem" }}
                  />
                  {itemPreviews[i] && (
                    <img
                      src={itemPreviews[i]}
                      alt={`Vorschau Artikel ${i + 1}`}
                      style={{
                        marginTop: "0.75em", width: "100%",
                        borderRadius: "6px", border: "1px solid #e2e8f0",
                        maxHeight: "220px", objectFit: "contain",
                        background: "#fdf7f4", display: "block",
                      }}
                    />
                  )}
                </div>
              ))}
            </div>
          ) : (
            /* ── Single-image fallback (manual mode or order has no items) ── */
            <label style={labelStyle}>
              Grafik (PNG oder JPG) *
              <input
                type="file"
                accept="image/png, image/jpeg"
                onChange={(e) => {
                  const selected = e.target.files[0];
                  if (previewUrl) URL.revokeObjectURL(previewUrl);
                  const newPreview = selected ? URL.createObjectURL(selected) : null;
                  if (selected) {
                    const match = guessOrderFromFilename(selected.name);
                    if (match) {
                      setAutoMatched(true);
                      selectOrder(match, selected, newPreview);
                      return; // selectOrder handles itemFiles; skip setFile
                    }
                  }
                  setFile(selected || null);
                  setPreviewUrl(newPreview);
                }}
                required={!selectedOrder}
                style={{ ...inputStyle, padding: "0.4em" }}
              />
            </label>
          )}

          {/* ── Extra manually-added slots (order mode only) ── */}
          {selectedOrder && (
            <div style={{ marginBottom: "1.25em" }}>
              {extraSlots.map((slot, i) => (
                <div key={i} style={{
                  border: "1px dashed #B8A09A", borderRadius: "8px",
                  padding: "0.9em 1em", marginTop: "0.75em",
                  background: slot.file ? "#f0fff4" : "#fafafa",
                }}>
                  <div style={{
                    display: "flex", justifyContent: "space-between",
                    alignItems: "center", marginBottom: "0.5em",
                  }}>
                    <span style={{
                      fontWeight: "600", fontSize: "0.875rem", color: "#2d3748",
                    }}>
                      Zusätzliche Grafik {i + 1}
                    </span>
                    <button
                      type="button"
                      onClick={() => removeExtraSlot(i)}
                      style={{
                        background: "none", border: "none",
                        color: "#a0aec0", cursor: "pointer", fontSize: "0.82rem",
                      }}
                    >
                      × entfernen
                    </button>
                  </div>
                  <input
                    type="text"
                    placeholder="Beschreibung (z. B. Variante A)"
                    value={slot.description}
                    onChange={(e) => updateExtraSlotDesc(i, e.target.value)}
                    style={{ ...inputStyle, marginBottom: "0.5em" }}
                  />
                  <input
                    type="file"
                    accept="image/png, image/jpeg"
                    onChange={(e) => updateExtraSlotFile(i, e.target.files[0])}
                    style={{ fontSize: "0.875rem" }}
                  />
                  {slot.preview && (
                    <img
                      src={slot.preview}
                      alt="Vorschau"
                      style={{
                        marginTop: "0.75em", width: "100%",
                        borderRadius: "6px", border: "1px solid #e2e8f0",
                        maxHeight: "220px", objectFit: "contain",
                        background: "#fdf7f4", display: "block",
                      }}
                    />
                  )}
                </div>
              ))}
              <button
                type="button"
                onClick={addExtraSlot}
                style={{
                  marginTop: "0.75em", padding: "0.45em 1em",
                  borderRadius: "6px", border: "1px dashed #B8A09A",
                  background: "#fdf7f4", color: "#B8A09A",
                  fontSize: "0.875rem", cursor: "pointer", width: "100%",
                }}
              >
                + Weitere Grafik hinzufügen
              </button>
            </div>
          )}

          {/* Single-image preview (manual mode) */}
          {previewUrl && !selectedOrder && (
            <div style={{ marginBottom: "1.25em" }}>
              <p style={{ margin: "0 0 0.5em", fontSize: "0.82rem",
                fontWeight: "600", color: "#4a5568" }}>
                Vorschau:
              </p>
              <img
                src={previewUrl}
                alt="Entwurf Vorschau"
                style={{
                  width: "100%", borderRadius: "8px",
                  border: "1px solid #e2e8f0",
                  maxHeight: "400px", objectFit: "contain",
                  background: "#fdf7f4",
                }}
              />
            </div>
          )}

          <button
            type="submit"
            disabled={uploading}
            style={{
              width: "100%", padding: "0.65em",
              background: uploading ? "#a0aec0" : "#4a2c2a",
              color: "#fff", border: "none", borderRadius: "6px",
              fontSize: "0.95rem", fontWeight: "600",
              cursor: uploading ? "not-allowed" : "pointer",
              marginTop: "0.5em",
            }}
          >
            {uploading ? "Wird hochgeladen…" : "Hochladen & Review-Link senden"}
          </button>
        </form>
      </div>
    </div>
  );
};

export default AdminUploadPage;
